/** @format */

import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

import { CustomHttpHeaders } from '@shared/data-access/enums';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { isPlatformBrowser } from '@angular/common';

@Injectable()
export class CommonInterceptor implements HttpInterceptor {
  private readonly platformId = inject(PLATFORM_ID);

  /**
   * @description
   * Function that acts as an Interceptor for all HTTP Requests.  It ensures that
   * all necessary WCS Auth Tokens are set on every HTTP Request as needed and provides
   * any common required Headers.
   */
  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Custom timeout
    let customTimeout: number;

    // Defaulted cloned request, in case nothing is modified
    let clonedRequest = request.clone();

    // Cloned headers
    let headers = clonedRequest.headers;

    /*
     * TODO: HACK UNTIL HTTPCLIENT OPTIONS ARE IMPLEMENTED -- SEE `https://github.com/angular/angular/issues/18155`
     */
    // Check to see if we need to ignore the `X-TIMEOUT` header
    const hasTimeoutHeader = request.headers.has(CustomHttpHeaders.X_TIMEOUT);
    const timeoutHeaderValue = request.headers.get(CustomHttpHeaders.X_TIMEOUT);
    const isTimeoutHeaderValid =
      hasTimeoutHeader &&
      timeoutHeaderValue &&
      !Number.isNaN(Number.parseInt(timeoutHeaderValue, 10));
    if (isTimeoutHeaderValid) {
      // Set the custom timeout
      customTimeout = Number.parseInt(timeoutHeaderValue, 10);

      // Modify the headers accordingly
      headers = headers.delete(CustomHttpHeaders.X_TIMEOUT);
    }

    // Check platform and check to see if we need to ignore the `X-PROXY-REQUEST` header
    /* istanbul ignore else */
    if (
      isPlatformBrowser(this.platformId) &&
      request.headers.has(CustomHttpHeaders.X_PROXY_REQUEST)
    ) {
      // Modify the headers accordingly
      headers = headers.delete(CustomHttpHeaders.X_PROXY_REQUEST);
    }

    // Clone the Request with new headers
    clonedRequest = clonedRequest.clone({
      headers,
    });

    // Send the newly created request
    return next.handle(clonedRequest).pipe(
      timeout(customTimeout || DCSG_CONSTANTS.timeouts.default),
      catchError((error) => {
        // Check to see if the error is a TimeoutError
        if (typeof error.name === 'string' && error.name.toLowerCase() === 'timeouterror') {
          // Return a timeout specific error with the URL
          return throwError(
            new HttpErrorResponse({
              error: `${DCSG_CONSTANTS.errorHandling.messagePrefixes.global.timeout}A timeout error has occurred for the URL "${clonedRequest.url}"`,
              headers: clonedRequest.headers,
              status: 499,
              statusText: 'CLIENT CLOSED REQUEST',
              url: clonedRequest.url,
            }),
          );
        } else {
          // Return standard error
          return throwError(error);
        }
      }),
    ) as Observable<HttpEvent<unknown>>;
  }
}
